import React from 'react';
import I18n from 'i18n-js';
import { 
  NudgeFastTruckIcon, 
  NudgeLocationPinIcon, 
  NudgeCartIcon, 
  NudgeGemIcon, 
  NudgeBoxIcon, 
  NudgeExploreIcon, 
} from '@reverbdotcom/cadence/icons/react';
import { RCNudgeProps, RCEyebrowProps } from '@reverbdotcom/cadence/components';
import { core_apimessages_Country as Country, ListingCardFields } from './rql';
import { IUser } from './components/user_context_provider';
import { experimentInt } from './user_context_helpers';
import experiments from './experiments';

interface NudgeConfig {
  returnPolicyDays: number;
  inOtherCartsCount: number;
  displayMultiple?: boolean;
  freeExpeditedShipping?: boolean;
  freeShipping?: boolean;
  greatValue?: boolean;
  localPickupOnly?: boolean;
  localPickupAvailable?: boolean;
  newListing?: boolean;
  country?: Country;
}

export const getListingCardNudgesWithLabels = ({ 
  inOtherCartsCount, 
  returnPolicyDays, 
  country = {}, 
}: NudgeConfig): { [k:string] : RCNudgeProps } => ({
  freeExpeditedShipping: { 
    label: I18n.t('commons.listingCardNudge.freeExpeditedShipping'), 
    icon: () => <NudgeFastTruckIcon />, 
  },
  freeShipping: { 
    label: I18n.t('commons.listingCardNudge.freeShipping'), 
    icon: () => <NudgeFastTruckIcon />, 
  },
  greatValue: { 
    label: I18n.t('commons.listingCardNudge.greatValue'), 
    icon: () => <NudgeGemIcon />,
  },
  inOtherCarts: { 
    label: I18n.t('commons.listingCardNudge.inOtherCarts', { count: (inOtherCartsCount) }), 
    icon: () => <NudgeCartIcon />,
  },
  localPickup: { 
    label: I18n.t('commons.listingCardNudge.localPickup'), 
    icon: () => <NudgeLocationPinIcon />,
  },
  localPickupAvailable: { 
    label: I18n.t('commons.listingCardNudge.localPickupAvailable'), 
    icon: () => <NudgeLocationPinIcon />,
  },
  returnPolicy: { 
    label: I18n.t('commons.listingCardNudge.returnPolicy', { returnPolicyDays: (returnPolicyDays || 0) }), icon: () => <NudgeBoxIcon />,
  },
  newListing: { 
    label: I18n.t('commons.listingCardNudge.newListing'), 
    icon: () => <NudgeExploreIcon />,
  },
  countryFlag: {
    label: country.name,
    icon: () => (
      <span className="listing-shop-country-nudge">
        <span className={`flag-icon flag-icon-${country.countryCode.toLowerCase()}`} />
      </span>
    ),
  },
});

function getNudgeDisplayCount(displayMultiple: boolean, newListingSignalMarketplaceExpGroup: number) {
  const defaultAmount = displayMultiple ? 2 : 1;

  if (displayMultiple && newListingSignalMarketplaceExpGroup === 1) {
    return defaultAmount + 1;
  }

  return defaultAmount;
}

export function getNudges(config: NudgeConfig, user: Partial<IUser>): RCNudgeProps[] {
  const listingCardNudges = getListingCardNudgesWithLabels(config);
  const applicableNudges = [];
  const requiredNudges = [];
  const newListingSignalMarketplaceExpGroup = experimentInt(user, experiments.RECENTLY_LISTED_SIGNAL_MARKETPLACE_V2);
  const inNudgeExp = newListingSignalMarketplaceExpGroup === 1 || newListingSignalMarketplaceExpGroup === 2;

  if (config.country) { requiredNudges.push(listingCardNudges.countryFlag); }
  if (config.freeExpeditedShipping) { applicableNudges.push(listingCardNudges.freeExpeditedShipping); }
  if (config.freeShipping && !config.freeExpeditedShipping) { applicableNudges.push(listingCardNudges.freeShipping); }
  if (config.returnPolicyDays >= 14) { applicableNudges.push(listingCardNudges.returnPolicy); }
  if (config.inOtherCartsCount > 0) { applicableNudges.push(listingCardNudges.inOtherCarts); }
  if (config.localPickupOnly) { applicableNudges.push(listingCardNudges.localPickup); }
  if (config.localPickupAvailable) { applicableNudges.push(listingCardNudges.localPickupAvailable); }
  if (config.greatValue) { applicableNudges.push(listingCardNudges.greatValue); }
  if (config.newListing && inNudgeExp) { applicableNudges.push(listingCardNudges.newListing); }

  const slicedApplicableNudges = applicableNudges.slice(0, getNudgeDisplayCount(config.displayMultiple, newListingSignalMarketplaceExpGroup));
  
  return [...requiredNudges, ...slicedApplicableNudges];
}

export function getEyebrow(listing: ListingCardFields.Fragment, showBump = true): RCEyebrowProps | undefined {
  const priceDrop = !!listing?.pricing?.originalPrice;

  if (listing?.bumped && showBump) {
    return { text: I18n.t('commons.listingCardEyebrows.bump'), type: 'highlight' };
  }

  if (priceDrop) {
    return { text: I18n.t('commons.listingCardEyebrows.priceDrop'), type: 'alert' };
  }
}
